/**
 * MQTT Command Handler — Module-level singleton + React hook
 *
 * Command processing runs at module level (not inside React lifecycle),
 * so it can never be killed by React re-renders or effect cleanup.
 * The React hook only reads the popup state via useSyncExternalStore.
 */
import { useSyncExternalStore, useCallback, useEffect } from 'react';
import { KeepAliveBridge } from '../native/KeepAliveBridge';
import * as MqttTelemetry from '../services/MqttTelemetry';
import type { MqttCommand } from '../services/MqttTelemetry';
import * as RemoteEmergency from '../services/RemoteEmergency';
import { applyDisplayDefaults } from './useDisplaySettings';

/** Map language codes to BCP-47 tags for TTS */
const SPEECH_LANG_MAP: Record<string, string> = {
  en: 'en-US', nl: 'nl-NL', fr: 'fr-FR', de: 'de-DE',
  'en-US': 'en-US', 'nl-NL': 'nl-NL', 'fr-FR': 'fr-FR', 'de-DE': 'de-DE',
};

/** Built-in sound URLs (bundled in public/) */
const SOUND_MAP: Record<string, string> = {
  ringtone: '/sounds/ringtone.mp3',
  alarm: '/sounds/alarm.mp3',
  notification: '/sounds/notification.mp3',
  chime: '/sounds/chime.mp3',
};

export interface ActivePopup {
  id: string;
  title: string;
  message: string;
  priority: 'normal' | 'urgent';
  url?: string;
  timestamp: string;
  autoDismissMs: number;
}

// ── Module-level state (survives React lifecycle) ────────────

let popups: ActivePopup[] = [];
const popupListeners = new Set<() => void>();
const processedIds = new Set<string>();

function notifyPopupListeners() {
  popupListeners.forEach(l => l());
}

function addPopup(popup: ActivePopup) {
  popups = [...popups, popup];
  notifyPopupListeners();

  // Auto-dismiss
  if (popup.autoDismissMs > 0) {
    setTimeout(() => dismissPopup(popup.id), popup.autoDismissMs);
  }
}

function dismissPopup(id: string) {
  const prev = popups;
  popups = popups.filter(p => p.id !== id);
  if (popups !== prev) {
    stopSound();
    notifyPopupListeners();
  }
}

function dismissAll() {
  popups = [];
  stopSound();
  notifyPopupListeners();
}

// ── Audio ────────────────────────────────────────────────────

let audioElement: HTMLAudioElement | null = null;

function playSound(sound: string, repeat: number = 0) {
  try {
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
    }

    const src = SOUND_MAP[sound] || sound;
    audioElement = new Audio(src);
    audioElement.volume = 1.0;

    let playCount = 0;
    const maxPlays = repeat + 1;

    audioElement.onended = () => {
      playCount++;
      if (playCount < maxPlays && audioElement) {
        audioElement.currentTime = 0;
        audioElement.play().catch(() => {});
      }
    };

    audioElement.play().then(() => {
      console.log('[MqttCommands] Playing sound:', src);
    }).catch((e) => {
      console.warn('[MqttCommands] Sound autoplay blocked:', e.message);
    });
  } catch (e) {
    console.warn('[MqttCommands] Sound error:', e);
  }
}

export function stopSound() {
  if (audioElement) {
    audioElement.pause();
    audioElement.currentTime = 0;
    audioElement = null;
  }
  KeepAliveBridge.stopTts();
}

function speakText(text: string, lang: string) {
  const mappedLang = SPEECH_LANG_MAP[lang] || lang || 'en-US';
  console.log('[MqttCommands] TTS:', text, 'lang:', mappedLang);
  KeepAliveBridge.speak(text, mappedLang);
}

// ── Navigation callback ──────────────────────────────────────

let navigateCallback: ((path: string) => void) | null = null;

export function setNavigateCallback(cb: (path: string) => void) {
  navigateCallback = cb;
}

// ── Command processing (module level — runs once, never dies) ─

MqttTelemetry.onCommand((cmd: MqttCommand) => {
  try {
    // Deduplicate
    if (processedIds.has(cmd.id)) return;
    processedIds.add(cmd.id);
    console.log('[MqttCommands] Processing:', cmd.type, cmd.id);

    // Limit dedup set
    if (processedIds.size > 200) {
      const entries = Array.from(processedIds);
      entries.splice(0, entries.length - 100).forEach(id => processedIds.delete(id));
    }

    switch (cmd.type) {
      case 'show_message': {
        addPopup({
          id: cmd.id,
          title: cmd.payload.title || 'Message',
          message: cmd.payload.message || '',
          priority: cmd.payload.priority || 'normal',
          url: cmd.payload.url as string | undefined,
          timestamp: cmd.timestamp,
          autoDismissMs: cmd.payload.duration ?? 15000,
        });
        if (cmd.payload.priority === 'urgent') {
          KeepAliveBridge.bringToFront();
        }
        playSound(cmd.payload.sound as string || 'notification');
        break;
      }

      case 'speak_tts': {
        const text = cmd.payload.text || cmd.payload.message || '';
        if (text) {
          KeepAliveBridge.bringToFront();
          speakText(text, cmd.payload.lang || 'nl');
          addPopup({
            id: cmd.id,
            title: 'Speaking',
            message: text,
            priority: 'normal',
            timestamp: cmd.timestamp,
            autoDismissMs: Math.max(5000, text.length * 80),
          });
        }
        break;
      }

      case 'play_ringtone': {
        const sound = cmd.payload.sound || 'ringtone';
        const repeat = cmd.payload.repeat ?? 0;
        playSound(sound as string, repeat as number);
        addPopup({
          id: cmd.id,
          title: 'Playing Sound',
          message: `Sound: ${sound}${repeat > 0 ? ` (${repeat + 1}x)` : ''}`,
          priority: 'normal',
          timestamp: cmd.timestamp,
          autoDismissMs: 10000,
        });
        break;
      }

      case 'navigate': {
        const url = cmd.payload.url;
        if (url && typeof url === 'string') {
          KeepAliveBridge.bringToFront();
          if (url.startsWith('http://') || url.startsWith('https://')) {
            navigateCallback?.(`/browser?url=${encodeURIComponent(url)}`);
          } else {
            navigateCallback?.(url);
          }
        }
        break;
      }

      case 'bring_to_front': {
        KeepAliveBridge.wakeScreen();
        KeepAliveBridge.bringToFront();
        break;
      }

      case 'refresh': {
        // Hard refresh — clear the HTTP cache (Cache Storage) and unregister any
        // service worker so the tablet fetches the NEW app version, then reload.
        // Pushed per-site from the portal after deploying a new build.
        void (async () => {
          try {
            if (typeof caches !== 'undefined' && caches.keys) {
              const keys = await caches.keys();
              await Promise.all(keys.map((k) => caches.delete(k)));
            }
          } catch { /* cache API unavailable */ }
          try {
            if (navigator.serviceWorker?.getRegistrations) {
              const regs = await navigator.serviceWorker.getRegistrations();
              await Promise.all(regs.map((r) => r.unregister()));
            }
          } catch { /* SW API unavailable */ }
          // Cache-bust the document request so a stale CDN/proxy copy of
          // index.html can't be served back.
          try {
            const u = new URL(window.location.href);
            u.searchParams.set('_v', Date.now().toString());
            window.location.replace(u.toString());
          } catch {
            window.location.reload();
          }
        })();
        break;
      }

      case 'reboot_webview': {
        setTimeout(() => { window.location.href = '/'; }, 300);
        break;
      }

      case 'emergency': {
        // Automation-driven tablet alarm state. The originating automation owns
        // the alarm; the tablet just reflects the state from the RemoteEmergency
        // store. 'alarm_mode' + seconds → count down to 0 and freeze (remote
        // mode, no confirm / no self-dispatch). 'reset' → back to normal.
        const emState = cmd.payload.state;
        if (emState === 'reset') {
          RemoteEmergency.reset();
          navigateCallback?.('/');
        } else if (emState === 'alarm_mode') {
          RemoteEmergency.setAlarmMode(cmd.payload.seconds);
          KeepAliveBridge.wakeScreen();
          KeepAliveBridge.bringToFront();
          navigateCallback?.('/emergency?remote=1');
        } else {
          console.warn('[MqttCommands] emergency: unknown state', emState);
        }
        break;
      }

      case 'display_settings': {
        // Per-location display defaults pushed from the portal. Apply theme /
        // clock / font-size / screensaver-allowed live.
        applyDisplayDefaults({
          theme: cmd.payload.theme,
          clockStyle: cmd.payload.clockStyle,
          fontSize: cmd.payload.fontSize,
          screensaverEnabled: cmd.payload.screensaverEnabled,
        });
        break;
      }

      default:
        console.warn('[MqttCommands] Unknown command type:', cmd.type);
    }
  } catch (e) {
    console.error('[MqttCommands] Error processing command:', cmd.type, e);
  }
});

// ── React hook (read-only, no subscriptions that can die) ────

export function useMqttCommands() {
  const currentPopups = useSyncExternalStore(
    (cb) => { popupListeners.add(cb); return () => popupListeners.delete(cb); },
    () => popups,
  );

  const handlePopupAction = useCallback((popup: ActivePopup) => {
    if (popup.url) {
      if (popup.url.startsWith('http://') || popup.url.startsWith('https://')) {
        navigateCallback?.(`/browser?url=${encodeURIComponent(popup.url)}`);
      } else {
        navigateCallback?.(popup.url);
      }
    }
    dismissPopup(popup.id);
  }, []);

  return {
    popups: currentPopups,
    dismissPopup,
    dismissAll,
    handlePopupAction,
  };
}
